import {
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Button,
  Theme,
  useMediaQuery,
} from '@mui/material';
import { useIntl } from 'react-intl';

import { Routes } from 'constants/routes.constants';
import { useAffiliatePartner } from 'services/stores/affiliate/hooks';

import { AffiliateHeadingLogo } from 'components/@affiliate/AffiliateLogo';
import LinkUnstyled from 'components/@common/LinkUnstyled';
import Icon from 'components/@icons/Icon';
import LogoNofomo from 'components/@navigation/LogoNofomo';

import BreadcrumbLabel from './BreadcrumbLabel';
import { Breadcrumb } from './types';

interface Props {
  items?: Array<Breadcrumb>;
}

const Breadcrumbs = ({ items = [] }: Props) => {
  const desktop = useMediaQuery<Theme>((theme) => theme.breakpoints.up('md'));
  const intl = useIntl();

  const affiliatePartner = useAffiliatePartner();
  const showAffiliateLogo = !!affiliatePartner?.headingLogo;

  return (
    <MuiBreadcrumbs
      aria-label="breadcrumb"
      maxItems={desktop ? 6 : 4}
      itemsBeforeCollapse={1}
      itemsAfterCollapse={desktop ? 4 : 1}
      separator={
        <Box
          sx={{
            display: {
              xs: showAffiliateLogo ? 'none' : 'flex',
              md: 'flex',
            },
          }}
        >
          <Icon
            name="ChevronRight"
            rotateInArabic
            sx={{
              xs: 16,
              md: 24,
              color: 'grey.600',
            }}
          />
        </Box>
      }
      sx={{ py: { xs: 0, md: 1 } }}
    >
      <Box
        aria-label={intl.formatMessage({
          id: 'navigation.home',
          defaultMessage: 'Home',
        })}
        component={LinkUnstyled}
        href={Routes.Home}
        sx={[
          {
            alignItems: 'center',
            display: 'flex',
          },
          (theme) => ({
            [theme.breakpoints.up('md')]: {
              svg: {
                transition: theme.transitions.create(['color']),
                color: 'text.secondary',
                '&:hover': {
                  color: 'primary.main',
                },
              },
            },
          }),
        ]}
      >
        <LogoNofomo sx={{ display: { xs: 'block', md: 'none' } }} />

        <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
          <AffiliateHeadingLogo />
        </Box>

        <Icon
          name="Home"
          sx={{
            display: { xs: 'none', md: 'block', fontSize: { xs: 16, md: 24 } },
          }}
        />
      </Box>

      {items?.map((breadcrumb, index) => {
        const isLastItem = index === items.length - 1;
        return (
          <Box
            key={index}
            component="span"
            sx={{
              ...(showAffiliateLogo && {
                display: {
                  xs: 'none',
                  md: 'flex',
                },
              }),
            }}
          >
            {!!breadcrumb.href && !isLastItem ? (
              <Button
                variant="text"
                component={LinkUnstyled}
                href={breadcrumb.href}
              >
                <BreadcrumbLabel breadcrumb={breadcrumb} />
              </Button>
            ) : (
              <BreadcrumbLabel breadcrumb={breadcrumb} isActive={isLastItem} />
            )}
          </Box>
        );
      })}
    </MuiBreadcrumbs>
  );
};

export default Breadcrumbs;
